/**
 * Captastic PDP gallery — show extra angles (back, sides, lifestyle)
 * for the selected colour, hide other colour packshots.
 *
 * Dawn hide_variants already drops other variants' featured media from
 * the main slider, but extra angles are stored as unattached product
 * media (attaching them would hide them). This script colour-filters
 * those extras. UK English comments only.
 *
 * Visibility is filter-in-place (class toggle) on the original thumbnail
 * strip — never clone or append a second strip. Dawn setActiveMedia may
 * prepend the active thumb; we re-filter after variantChange so state
 * stays correct.
 *
 * Headwear UK note: filenames use short colour tokens (Green) while option
 * values use prefixes (Hiviz Green). Hero files use Mixed/Main/etc. which
 * are not colours — treat as generic media. Never blank the gallery.
 */
(function () {
  const COLOUR_NAMES = /^(colour|color|colourway|colorway)$/i;
  const ABS_RE = /ABS-COLOUR:[^:\]]+:([A-Za-z0-9]+)/i;
  const ANGLE_RE = /(side|back|rear|extra|left|right|lifestyle)/i;
  const FRONT_RE = /\bfront\b/i;

  /** Filename tokens that are layout/hero labels, not colour names. */
  const NON_COLOUR_FILE_TOKENS = new Set([
    'mixed',
    'main',
    'hero',
    'pack',
    'packshot',
    'lifestyle',
    'default',
    'primary',
    'cover',
    'swatch',
    'group',
    'all',
    'assortment',
    'range',
  ]);

  /**
   * Option-value prefixes stripped when comparing to a short file colour
   * (Hiviz Green ↔ Green). Exact match is always tried first.
   */
  const COLOUR_PREFIXES = [
    'hiviz',
    'hivis',
    'hivi',
    'fluorescent',
    'fluro',
    'fluoro',
    'bright',
  ];

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '');
  }

  const PRODUCT_TOKENS = new Set([
    'mesh',
    'trucker',
    'cap',
    'stock',
    'access',
    'five',
    'panel',
    'surf',
    'bucket',
    'hat',
    'cuff',
    'beanie',
    'knit',
    'icon',
    'high',
    'profile',
  ]);

  function parseFilenameColour(src) {
    if (!src) return '';
    const file = decodeURIComponent(src.split('/').pop() || '').split('?')[0];
    let stem = file.replace(/\.[a-z0-9]+$/i, '').replace(/_[0-9a-f-]{8,}$/i, '');
    const parts = stem.split('_').filter(Boolean);
    if (parts.length && /^\d{3,5}$/.test(parts[0])) parts.shift();
    if (parts.length && /^(SIDE|BACK|FRONT|REAR)$/i.test(parts[parts.length - 1])) parts.pop();
    const colour = [];
    while (parts.length && !PRODUCT_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
      colour.unshift(parts.pop());
    }
    if (!colour.length) return '';
    const joined = norm(colour.join(' '));
    // Hero / packshot / mixed layout files are not colour labels.
    // Also treat "20Main", "01Hero" etc. (digits + non-colour token) as generic.
    if (isNonColourFileToken(joined)) return '';
    if (colour.every((p) => isNonColourFileToken(norm(p)))) return '';
    return joined;
  }

  function isNonColourFileToken(n) {
    if (!n) return false;
    if (NON_COLOUR_FILE_TOKENS.has(n)) return true;
    const stripped = n.replace(/^\d+/, '');
    return Boolean(stripped) && NON_COLOUR_FILE_TOKENS.has(stripped);
  }

  function colourFromText(text, src) {
    const abs = String(text || '').match(ABS_RE);
    const fromAbs = abs ? norm(abs[1]) : '';
    const emdash = String(text || '').match(/[—-]\s*([^[\]]+?)(?:\s*\[|$)/);
    let fromName = '';
    if (emdash) {
      const name = emdash[1].replace(/\s+(front|back|side|rear|extra|view).*$/i, '').trim();
      if (name && !/^extra/i.test(name)) fromName = norm(name);
    }
    const fromFile = parseFilenameColour(src);
    return { code: fromAbs, name: fromName, file: fromFile };
  }

  function itemColour(el) {
    if (!el) return { code: '', name: '', file: '' };
    const img = el.querySelector('img');
    const alt =
      (img && (img.getAttribute('alt') || img.getAttribute('aria-label'))) ||
      el.getAttribute('aria-label') ||
      '';
    const src = (img && (img.currentSrc || img.src || img.getAttribute('src'))) || '';
    return colourFromText(alt, src);
  }

  function itemAlt(el) {
    const img = el && el.querySelector('img');
    return (img && (img.getAttribute('alt') || '')) || '';
  }

  function selectedColour(root, variant) {
    const names = [];
    if (variant) {
      (variant.selectedOptions || []).forEach((o) => {
        if (o && COLOUR_NAMES.test(o.name || '')) names.unshift(o.value);
      });
      // product.js variants expose option1/options without names — Colour is option1
      if (!names.length && variant.option1) names.push(variant.option1);
    }
    root.querySelectorAll('variant-selects select, variant-radios select').forEach((sel) => {
      const label = (sel.getAttribute('name') || sel.getAttribute('aria-label') || '').replace(
        /^options\[|\]$/g,
        ''
      );
      if (COLOUR_NAMES.test(label) && sel.value) names.unshift(sel.value);
    });
    root.querySelectorAll('fieldset legend, .form__label').forEach((leg) => {
      if (!COLOUR_NAMES.test((leg.textContent || '').trim())) return;
      const fs = leg.closest('fieldset');
      const checked = fs && fs.querySelector('input:checked');
      if (checked) names.unshift(checked.value || checked.getAttribute('data-option-value') || '');
    });
    return names.map(norm).filter(Boolean);
  }

  /**
   * Strip known hi-vis / fluorescent prefixes from a normalised colour so
   * "hivizgreen" → "green". Only whole-prefix strip at the start.
   */
  function stripColourPrefixes(n) {
    let s = String(n || '');
    let changed = true;
    while (changed && s) {
      changed = false;
      for (const p of COLOUR_PREFIXES) {
        if (s.startsWith(p) && s.length > p.length) {
          s = s.slice(p.length);
          changed = true;
          break;
        }
      }
    }
    return s;
  }

  /**
   * Exact normalised match preferred. Also allow selected-with-prefix-stripped
   * === file colour (Hiviz Green ↔ Green). Do NOT use substring includes —
   * "darkgrey".includes("grey") caused Grey thumbs to show for Dark Grey.
   */
  function colourKeysEqual(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    const aStripped = stripColourPrefixes(a);
    const bStripped = stripColourPrefixes(b);
    // Selected with prefix stripped equals file (or vice versa)
    if (aStripped && aStripped === b) return true;
    if (bStripped && bStripped === a) return true;
    if (aStripped && bStripped && aStripped === bStripped) return true;
    return false;
  }

  function matchesColour(meta, selected) {
    if (!selected.length) return true;
    const primary = [meta.name, meta.file].map(norm).filter(Boolean);
    if (primary.length) {
      return primary.some((k) => selected.some((s) => colourKeysEqual(s, k)));
    }
    const code = norm(meta.code);
    if (!code) return true; // generic / lifestyle / untagged / Mixed hero — keep
    return selected.some((s) => colourKeysEqual(s, code));
  }

  function isAngleShot(el) {
    const alt = itemAlt(el);
    return ANGLE_RE.test(alt) && !FRONT_RE.test(alt.replace(/extra/gi, ''));
  }

  function isFrontPackshot(el) {
    const alt = itemAlt(el);
    if (ANGLE_RE.test(alt) && !FRONT_RE.test(alt)) return false;
    // "Colour front" or plain colour name without angle words → packshot
    return FRONT_RE.test(alt) || !ANGLE_RE.test(alt);
  }

  function isFeaturedOrActive(el) {
    if (!el) return false;
    if (el.classList.contains('is-active')) return true;
    if (el.classList.contains('thumbnail-list_item--variant')) return true;
    // Dawn marks the current slide; keep it even when colour parse fails.
    if (el.getAttribute('data-media-id') && el.classList.contains('is-active')) return true;
    return false;
  }

  /**
   * When the variant featured thumb is visible for this colour, hide extra
   * unattached "front" packshots of the same colour (same image uploaded
   * twice — once on the variant, once as free media). Keep true angles.
   */
  function dedupeFrontPackshots(gallery) {
    const visible = [
      ...gallery.querySelectorAll('.thumbnail-list__item:not(.captastic-gallery-hidden)'),
    ];
    const hasVariant = visible.some((t) => t.classList.contains('thumbnail-list_item--variant'));
    if (!hasVariant) return;
    visible.forEach((t) => {
      if (t.classList.contains('thumbnail-list_item--variant')) return;
      if (isFrontPackshot(t) && !isAngleShot(t)) {
        t.classList.add('captastic-gallery-hidden');
      }
    });
  }

  /**
   * Never leave a blank main gallery. If every .product__media-item is
   * hidden, unhide all (or at least the featured/active ones).
   */
  function ensureNotBlank(gallery) {
    const mediaItems = [...gallery.querySelectorAll('.product__media-item')];
    if (!mediaItems.length) return;
    const anyVisible = mediaItems.some((el) => !el.classList.contains('captastic-gallery-hidden'));
    if (anyVisible) return;

    const featured = mediaItems.filter(isFeaturedOrActive);
    const restore = featured.length ? featured : mediaItems;
    restore.forEach((el) => el.classList.remove('captastic-gallery-hidden'));

    // Mirror: unhide matching thumbs by media id when possible
    const ids = new Set(restore.map((el) => el.getAttribute('data-media-id')).filter(Boolean));
    gallery.querySelectorAll('.thumbnail-list__item').forEach((t) => {
      const tid = t.getAttribute('data-target') || t.getAttribute('data-media-id') || '';
      const match =
        isFeaturedOrActive(t) ||
        [...ids].some((id) => tid && (tid === id || tid.endsWith(id) || id.endsWith(tid)));
      if (match || !ids.size) t.classList.remove('captastic-gallery-hidden');
    });
  }

  function filterGallery(root, variant) {
    const gallery = root.querySelector('media-gallery');
    if (!gallery) return;
    const selected = selectedColour(root, variant);
    const items = gallery.querySelectorAll('.product__media-item, .thumbnail-list__item');
    items.forEach((el) => {
      // Never hide the currently active / variant featured slide — Dawn
      // already chose it for this variant; blanking it causes the empty column.
      if (isFeaturedOrActive(el)) {
        el.classList.remove('captastic-gallery-hidden');
        return;
      }
      const meta = itemColour(el);
      const show = matchesColour(meta, selected);
      el.classList.toggle('captastic-gallery-hidden', !show);
    });
    dedupeFrontPackshots(gallery);
    ensureNotBlank(gallery);

    let visibleThumbs = 0;
    gallery.querySelectorAll('.thumbnail-list__item').forEach((el) => {
      if (!el.classList.contains('captastic-gallery-hidden')) visibleThumbs += 1;
    });
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbs) thumbs.classList.toggle('captastic-thumbs-empty', visibleThumbs <= 1);
  }

  function scheduleFilter(root, variant) {
    filterGallery(root, variant);
    // Dawn setActiveMedia(..., prepend) reorders thumbs after variantChange;
    // re-apply once the prepend has landed so first-child CSS cannot fight us.
    requestAnimationFrame(() => {
      filterGallery(root, variant);
      requestAnimationFrame(() => filterGallery(root, variant));
    });
  }

  /**
   * Prefer product.js current variant (option1 = Colour) so first paint
   * matches the selected colour before any change event.
   */
  function initialVariant(root) {
    try {
      // Prefer VariantSelects currentVariant when Dawn has already resolved it.
      const vs = root.querySelector('variant-selects, variant-radios');
      if (vs && typeof vs.currentVariant === 'object' && vs.currentVariant) {
        return vs.currentVariant;
      }
      const input =
        root.querySelector('input[name="id"]') ||
        document.querySelector(
          'product-info input[name="id"], form[action*="/cart/add"] input[name="id"]'
        );
      const id = input && input.value;
      if (id && window.meta && window.meta.product && Array.isArray(window.meta.product.variants)) {
        const found = window.meta.product.variants.find((v) => String(v.id) === String(id));
        if (found) {
          return {
            option1: found.option1 || (found.options && found.options[0]),
            ...found,
          };
        }
      }
      // Fallback: selectedColour reads checked radio / select from the DOM.
      return null;
    } catch (_) {
      return null;
    }
  }

  function bind(root) {
    if (!root || root.dataset.captasticGalleryBound) return;
    root.dataset.captasticGalleryBound = 'true';
    filterGallery(root, initialVariant(root));

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
        const sid = root.dataset.section;
        if (evt?.data?.sectionId && sid && String(evt.data.sectionId) !== String(sid)) return;
        scheduleFilter(root, evt?.data?.variant);
      });
    }

    // Delegated: Dawn replaces <variant-selects> on each update, so do not
    // bind directly to the inner element.
    root.addEventListener('change', (e) => {
      if (e.target && e.target.closest && e.target.closest('variant-selects, variant-radios')) {
        scheduleFilter(root, null);
      }
    });
  }

  function init() {
    document.querySelectorAll('product-info').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
